import type { ReactNode } from "react"

import { formatCurrency } from "~/utils/finance/format"

/**
 * The System Pricing step's breakdown and totals, for any proposal kind. Rows
 * arrive already grouped and in display order: Battery groups its stored line
 * items, Solar passes the server's groups. These pieces format and lay out
 * what they are given and compute no rows.
 */

/** One breakdown row. `amount` is null exactly when the row is names-only. */
export type PricingBreakdownRow = {
  key: string
  label: string
  amount: number | null
  /** $/W, shown only when the table has a $/W column. */
  ppw: number | null
  namesOnly: boolean
}

export type PricingBreakdownGroup = {
  key: string
  label: string
  rows: readonly PricingBreakdownRow[]
}

/** `$1.25/W`. */
export const formatPricePerWatt = (ppw: number): string =>
  `${formatCurrency(ppw)}/W`

const bodyCellClass = `border-b border-border py-3 text-[length:var(--step--1)]`
/** Fixed Total column width so item names keep the remaining table width. */
const PRICE_COLUMN_CLASS = `w-32`
/** With a $/W column beside it, both narrow on phones so names keep room. */
const PRICE_WITH_PPW_COLUMN_CLASS = `w-28 sm:w-32`
const PPW_COLUMN_CLASS = `w-20 sm:w-24`

const BreakdownRow = ({
  row,
  showPpw,
}: {
  row: PricingBreakdownRow
  showPpw: boolean
}): JSX.Element => (
  <tr>
    <th
      scope="row"
      className={`${bodyCellClass} min-w-0 pr-4 text-left font-semibold text-foreground`}
    >
      <span className="block break-words">{row.label}</span>
    </th>
    <td
      className={`${bodyCellClass} ${showPpw ? PRICE_WITH_PPW_COLUMN_CLASS : PRICE_COLUMN_CLASS} pr-4 text-right tabular-nums text-foreground`}
    >
      {row.namesOnly || row.amount == null ? null : formatCurrency(row.amount)}
    </td>
    {showPpw ? (
      <td
        className={`${bodyCellClass} ${PPW_COLUMN_CLASS} pr-4 text-right tabular-nums text-muted`}
      >
        {row.namesOnly || row.ppw == null ? null : formatPricePerWatt(row.ppw)}
      </td>
    ) : null}
  </tr>
)

/** A captioned table of row groups, each under its uppercase heading. */
export const PricingGroupsTable = ({
  caption,
  groups,
  showPpw = false,
  className,
}: {
  caption: string
  groups: readonly PricingBreakdownGroup[]
  showPpw?: boolean
  className?: string
}): JSX.Element => (
  <table
    className={`w-full table-fixed border-separate border-spacing-y-0${className ? ` ${className}` : ``}`}
  >
    <caption className="sr-only">{caption}</caption>
    <colgroup>
      <col />
      <col
        className={showPpw ? PRICE_WITH_PPW_COLUMN_CLASS : PRICE_COLUMN_CLASS}
      />
      {showPpw ? <col className={PPW_COLUMN_CLASS} /> : null}
    </colgroup>
    <thead className="sr-only">
      <tr>
        <th scope="col">Item</th>
        <th scope="col">Total</th>
        {showPpw ? <th scope="col">$/W</th> : null}
      </tr>
    </thead>
    {groups.map((group, index) => (
      <tbody key={group.key} data-slot={`pricing-group-${group.key}`}>
        <tr>
          <th
            scope="rowgroup"
            colSpan={showPpw ? 3 : 2}
            className={`border-b-2 border-border-strong pb-1.5 text-left text-[length:var(--step--2)] font-semibold uppercase tracking-wider text-muted ${
              index === 0 ? `pt-0` : `pt-6`
            }`}
          >
            {group.label}
          </th>
        </tr>
        {group.rows.map((row) => (
          <BreakdownRow key={row.key} row={row} showPpw={showPpw} />
        ))}
      </tbody>
    ))}
  </table>
)

/**
 * The scrolling body of stored System Pricing: the breakdown table, then
 * anything the kind adds beneath it.
 */
export const PricingBreakdownTable = ({
  groups,
  showPpw = false,
  children = null,
}: {
  groups: readonly PricingBreakdownGroup[]
  showPpw?: boolean
  children?: ReactNode
}): JSX.Element => (
  <div
    data-slot="pricing-body"
    className="min-h-0 flex-1 overflow-y-auto pr-5 [scrollbar-gutter:stable]"
  >
    <PricingGroupsTable
      caption="Stored System Pricing"
      groups={groups}
      showPpw={showPpw}
    />
    {children}
  </div>
)

const TotalsRow = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): JSX.Element => (
  <tr>
    <th
      scope="row"
      className="py-1.5 text-left text-[length:var(--step--1)] font-medium text-foreground"
    >
      {label}
    </th>
    <td className="py-1.5 text-right text-[length:var(--step--1)] tabular-nums text-foreground">
      {children}
    </td>
  </tr>
)

/**
 * The totals card under the breakdown: margin (its cell is the caller's, an
 * input or a figure), sales tax, excise when there is any, and Total Cost with
 * an optional $/W and a note such as an estimate label.
 */
export const PricingTotals = ({
  margin,
  salesTax,
  exciseTax,
  totalCost,
  totalPpw = null,
  totalNote = null,
}: {
  margin: ReactNode
  salesTax: number
  /** Null hides the row. */
  exciseTax: number | null
  totalCost: number
  totalPpw?: number | null
  totalNote?: ReactNode
}): JSX.Element => (
  <div
    data-slot="pricing-totals-card"
    className="mt-3 shrink-0 rounded-lg border border-border bg-bp-elevated-2 px-5 py-4"
  >
    <table
      data-slot="pricing-totals"
      className="w-full shrink-0 table-fixed border-separate border-spacing-y-0"
    >
      <caption className="sr-only">Pricing totals</caption>
      <tbody>
        <TotalsRow label="Margin">{margin}</TotalsRow>
        <TotalsRow label="Sales Tax">{formatCurrency(salesTax)}</TotalsRow>
        {exciseTax != null ? (
          <TotalsRow label="General Excise Tax">
            {formatCurrency(exciseTax)}
          </TotalsRow>
        ) : null}
      </tbody>
      <tfoot>
        <tr>
          <th
            scope="row"
            className="border-t border-border-strong pt-4 text-left text-[length:var(--step--1)] font-semibold text-foreground"
          >
            Total Cost
          </th>
          <td className="border-t border-border-strong pt-4 text-right text-[length:var(--step-1)] font-semibold tabular-nums text-bp-gold">
            {formatCurrency(totalCost)}
            {totalPpw != null ? (
              <span
                data-slot="pricing-total-ppw"
                className="block text-[length:var(--step--2)] font-medium text-muted"
              >
                {formatPricePerWatt(totalPpw)}
              </span>
            ) : null}
            {totalNote != null ? (
              <span
                data-slot="pricing-total-note"
                className="block text-[length:var(--step--2)] font-medium text-muted"
              >
                {totalNote}
              </span>
            ) : null}
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
)
